import {getTestProps} from '@benchling/js-test-utils';
import {Anchor} from '@mantine/core';
import {createStyles} from '@mantine/core';
import React from 'react';

import navigateToURL from 'lib/navigateToURL';
import {FONT_PROXIMA_13} from 'styles/FontConstants';

const useStyles = createStyles((theme) => ({
  breadcrumbs: {
    ...FONT_PROXIMA_13,
    display: 'flex',
    alignItems: 'center',
  },
  breadcrumb: {
    color: theme.colors.gray[7],
    cursor: 'pointer',
    ':hover': {
      color: theme.colors.blue[4],
    },
  },
  nonInteractive: {
    color: theme.colors.gray[7],
    cursor: 'default',
  },
  separator: {
    margin: '0 8px',
    display: 'flex',
    alignItems: 'center',
    color: theme.colors.gray[7],
  },
  separatorSvg: {
    height: '1em',
    width: 'auto',
    fill: 'currentColor',
  },
}));

function DefaultSeparator({className}: {className: string}): JSX.Element {
  return (
    <svg viewBox="0 0 10 24" className={className}>
      <path d="M6.91528 0.831914C7.08048 0.233091 7.7 -0.118951 8.29883 0.0460256C8.89765 0.211218 9.24969 0.830739 9.08472 1.42957L3.08472 23.1796C2.91952 23.7784 2.3 24.1304 1.70117 23.9655C1.10235 23.8003 0.750307 23.1807 0.915284 22.5819L6.91528 0.831914Z" />
    </svg>
  );
}

export interface BreadcrumbItem {
  /** The text to display for this breadcrumb */
  label: string;
  /** The URL to navigate to when clicked. If not provided, breadcrumb will not be clickable */
  href?: string;
  /**
   * Custom click handler. Takes priority over href for unmodified clicks.
   * If href is also provided, modifier clicks are delegated to the browser so
   * standard new-tab/new-window behavior is preserved.
   */
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export interface BreadcrumbProps {
  /** Array of breadcrumb items to display */
  items: Array<BreadcrumbItem>;
  /**
   * Separator to use between breadcrumbs. Defaults to a forward slash SVG icon.
   * Can be a string or React element.
   */
  separator?: React.ReactNode;
  /**
   * Whether the last item should be interactive/clickable. Defaults to false.
   * When true, also shows a separator after the last item.
   */
  isLastItemInteractable?: boolean;
}

/**
 * A breadcrumb navigation component using Mantine's Anchor.
 *
 * Non-interactive items are rendered as spans, interactive items as anchors.
 * When isLastItemInteractable is true, the last item will be clickable and
 * followed by a separator (e.g., "Task Schemas /"). This lets the caller control
 * how they render the last item. Maybe they want it to be a header
 *
 * Usage:
 * ```tsx
 * <Breadcrumb
 *   items={[
 *     { label: 'Home', href: '/' },
 *     { label: 'Settings', href: '/settings' },
 *     { label: 'Current Page' }
 *   ]}
 * />
 * ```
 */
export default function Breadcrumb({
  items,
  separator,
  isLastItemInteractable = false,
}: BreadcrumbProps): JSX.Element {
  const {classes} = useStyles();
  const separatorElement = separator ?? <DefaultSeparator className={classes.separatorSvg} />;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, item: BreadcrumbItem): void => {
    const isModifierClick = e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1;
    // Only let the browser handle modifier clicks when there's a real href to open
    // in a new tab. For onClick-only items (no href), modifier clicks have no target to open,
    // so we fall through and run the click handler like a plain click.
    if (isModifierClick && item.href != null) {
      return;
    }

    e.preventDefault();
    if (item.onClick) {
      item.onClick(e);
    } else if (item.href != null) {
      navigateToURL(item.href);
    }
  };

  return (
    <div className={classes.breadcrumbs}>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const hasInteraction = item.href != null || item.onClick != null;
        const shouldBeInteractive = hasInteraction && (isLastItemInteractable || !isLast);
        // Show separator after non-last items, or after last item if it's interactive
        const showSeparator = !isLast || isLastItemInteractable;
        // Without href, the anchor has no implicit link role and isn't focusable.
        // Restore both so onClick-only items are accessible to keyboard / AT users.
        const isOnClickOnly = item.href == null && item.onClick != null;

        if (shouldBeInteractive) {
          return (
            <React.Fragment key={index}>
              <Anchor
                href={item.href}
                role={isOnClickOnly ? 'link' : undefined}
                tabIndex={isOnClickOnly ? 0 : undefined}
                className={classes.breadcrumb}
                underline={false}
                onClick={(e) => {
                  handleClick(e, item);
                }}
                onKeyDown={(e) => {
                  if (isOnClickOnly && e.key === 'Enter') {
                    e.preventDefault();
                    e.currentTarget.click();
                  }
                }}
              >
                {item.label}
              </Anchor>
              {showSeparator && (
                <span
                  className={classes.separator}
                  {...getTestProps({component: 'Breadcrumb', element: 'separator'})}
                >
                  {separatorElement}
                </span>
              )}
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={index}>
            <span className={classes.nonInteractive}>{item.label}</span>
            {showSeparator && (
              <span
                className={classes.separator}
                {...getTestProps({component: 'Breadcrumb', element: 'separator'})}
              >
                {separatorElement}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
